import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileText, AlertCircle, CheckCircle, Info, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateCampaignName, getEmptyCampaignForm } from '../../utils/campaignNameGenerator';
import './CampaignUploadModal.css';

// Use fetch + absolute /api URL (not axios) so baseURL cannot become /api/api/...
const getCampaignUploadUrl = () =>
  `${window.location.origin}/api/campaign/upload-campaign-csv-simple`;

const CampaignUploadModal = ({
  showModal,
  onClose,
  onUploadSuccess
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [isManuallyEditedCampaignName, setIsManuallyEditedCampaignName] = useState(false);
  
  // Campaign form state (date defaults to today; user can change)
  const [campaignForm, setCampaignForm] = useState(() => getEmptyCampaignForm());

  const handleFileSelect = async (file) => {
    setFileError('');
    setUploadResult(null);
    setPreviewRows([]);
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setFileError('Please upload a CSV file');
      setSelectedFile(null);
      return;
    }

    // Validate file size (max 50MB as per plan)
    if (file.size > 50 * 1024 * 1024) {
      setFileError('File size must be less than 50MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    // Optional: Preview first few rows for format validation
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const preview = lines.slice(0, 6); // First 6 rows (header + 5 data rows)
      setPreviewRows(preview);
    } catch (err) {
      console.error('Error reading file preview:', err);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError('');
    setUploadResult(null);
    setPreviewRows([]);
    setUploadProgress(0);
  };

  const handleCampaignFormChange = (field, value) => {
    setCampaignForm(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If user is directly editing the campaign name field, mark it as manually edited
      if (field === 'campaignName') {
        setIsManuallyEditedCampaignName(true);
        return updated;
      }
      
      // Auto-generate campaign name when source, objective, campaignDate, or coupon changes
      // But only if the user hasn't manually edited the campaign name
      if (field === 'source' || field === 'objective' || field === 'campaignDate' || field === 'coupon') {
        // Don't auto-generate if user has manually edited the campaign name
        if (!isManuallyEditedCampaignName) {
          const generatedName = generateCampaignName({
            campaignDate: field === 'campaignDate' ? value : updated.campaignDate,
            source: field === 'source' ? value : updated.source,
            objective: field === 'objective' ? value : updated.objective,
            coupon: field === 'coupon' ? value : updated.coupon
          });
          
          // Only update if we have enough data to generate a name
          if (generatedName) {
            updated.campaignName = generatedName;
          }
        }
      }
      
      return updated;
    });
  };

  const handleOfferChange = (offer) => {
    setCampaignForm(prev => ({
      ...prev,
      offers: prev.offers.includes(offer)
        ? prev.offers.filter(o => o !== offer)
        : [...prev.offers, offer]
    }));
  };

  const validateCampaignForm = () => {
    if (!campaignForm.campaignName || !campaignForm.source || 
        !campaignForm.pricePerMessage || !campaignForm.campaignDate || 
        !campaignForm.objective) {
      return 'Please fill in all required campaign fields';
    }

    if (campaignForm.source === 'others' && !campaignForm.sourceRemarks) {
      return 'Please provide remarks for "others" source';
    }

    if (campaignForm.offers.includes('Other') && !campaignForm.otherOfferDetails) {
      return 'Please provide details for "Other" offer';
    }

    return null;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setFileError('Please select a file first');
      return;
    }

    // Validate campaign form
    const formError = validateCampaignForm();
    if (formError) {
      setFileError(formError);
      toast.error(formError);
      return;
    }

    setUploading(true);
    setFileError('');
    setUploadProgress(0);

    try {
      // Use the campaign name from the form - respect manual edits
      // Only regenerate if the form name is empty or matches auto-generated pattern
      const autoGeneratedName = generateCampaignName({
        campaignDate: campaignForm.campaignDate,
        source: campaignForm.source,
        objective: campaignForm.objective,
        coupon: campaignForm.coupon
      });
      
      const finalCampaignName = campaignForm.campaignName || autoGeneratedName;

      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Add campaign info to form data
      formData.append('campaignName', finalCampaignName);
      formData.append('source', campaignForm.source);
      formData.append('sourceRemarks', campaignForm.sourceRemarks || '');
      formData.append('pricePerMessage', campaignForm.pricePerMessage);
      formData.append('campaignDate', campaignForm.campaignDate);
      formData.append('objective', campaignForm.objective);
      formData.append('offers', JSON.stringify(campaignForm.offers));
      formData.append('otherOfferDetails', campaignForm.otherOfferDetails || '');
      formData.append('coupon', campaignForm.coupon || '');

      const sessionId = localStorage.getItem('sessionId');
      const uploadUrl = getCampaignUploadUrl();
      setUploadProgress(25);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : {},
        body: formData,
      });
      setUploadProgress(90);

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const errorMessage =
          data?.message ||
          data?.error ||
          `Upload failed (${response.status})`;
        setUploadResult({
          success: false,
          message: errorMessage,
          errors: data?.errors || [],
        });
        toast.error(errorMessage);
        return;
      }

      if (data?.success) {
        toast.success('Campaign uploaded! Refreshing dashboard…');

        // Close modal and refresh immediately — analytics are ready (awaited on backend)
        handleClose();
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        setUploadResult({
          success: false,
          message: data?.message || 'Upload failed',
          errors: data?.errors || []
        });
        toast.error(data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading campaign CSV:', error);
      const errorMessage = error.message || 'Failed to upload campaign CSV';
      setUploadResult({
        success: false,
        message: errorMessage,
        errors: []
      });
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileError('');
    setUploadResult(null);
    setPreviewRows([]);
    setUploadProgress(0);
    setIsManuallyEditedCampaignName(false); // Reset manual edit flag
    setCampaignForm(getEmptyCampaignForm());
    onClose();
  };

  const downloadSampleCSV = () => {
    const sampleCSV = `email,phone
customer1@example.com,
,9876543210
customer3@example.com,9876543211
customer4@example.com,
,9988776655`;

    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'campaign-customers-sample.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Sample CSV downloaded');
  };

  return (
    <AnimatePresence>
      {showModal && (
        <>
          <motion.div
            className="campaign-upload-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="campaign-upload-modal"
            initial={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, scale: 0.9, x: '-50%', y: '-50%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="campaign-upload-modal-header">
              <h2 className="campaign-upload-modal-title">Upload Campaign CSV</h2>
              <button className="campaign-upload-modal-close" onClick={handleClose}>×</button>
            </div>

            <div className="campaign-upload-modal-content">
              {/* Campaign Information Form */}
              <div className="campaign-form-section">
                <h3 className="form-section-title">Campaign Information</h3>
                <div className="campaign-form-grid">
                  <div className="form-group">
                    <label>Source of Campaign *</label>
                    <select
                      value={campaignForm.source}
                      onChange={(e) => handleCampaignFormChange('source', e.target.value)}
                      className="form-input"
                      disabled={uploading}
                    >
                      <option value="">Select source</option>
                      <option value="email">Email</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="SMS">SMS</option>
                      <option value="calling">Calling</option>
                      <option value="others">Others</option>
                    </select>
                  </div>

                  {campaignForm.source === 'others' && (
                    <div className="form-group">
                      <label>Source Remarks *</label>
                      <input
                        type="text"
                        value={campaignForm.sourceRemarks}
                        onChange={(e) => handleCampaignFormChange('sourceRemarks', e.target.value)}
                        placeholder="Please specify the source"
                        className="form-input"
                        disabled={uploading}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Price per Message (₹) *</label>
                    <input
                      type="number"
                      value={campaignForm.pricePerMessage}
                      onChange={(e) => handleCampaignFormChange('pricePerMessage', e.target.value)}
                      placeholder="Enter price per message"
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={uploading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Date of Campaign *</label>
                    <input
                      type="date"
                      value={campaignForm.campaignDate}
                      onChange={(e) => handleCampaignFormChange('campaignDate', e.target.value)}
                      className="form-input"
                      disabled={uploading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Objective of Campaign *</label>
                    <select
                      value={campaignForm.objective}
                      onChange={(e) => handleCampaignFormChange('objective', e.target.value)}
                      className="form-input"
                      disabled={uploading}
                    >
                      <option value="">Select objective</option>
                      <option value="Abandoned">Abandoned</option>
                      <option value="Acquisition">Acquisition</option>
                      <option value="Retention">Retention</option>
                      <option value="Issue orders">Issue orders</option>
                      <option value="Dead User">Dead User</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Offers (Multiple selection allowed)</label>
                    <div className="checkbox-group">
                      {['1 Rs deal', '10% code', 'Other', 'None'].map(offer => (
                        <label key={offer} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={campaignForm.offers.includes(offer)}
                            onChange={() => handleOfferChange(offer)}
                            className="checkbox-input"
                            disabled={uploading}
                          />
                          <span className="checkbox-text">{offer}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {campaignForm.offers.includes('Other') && (
                    <div className="form-group">
                      <label>Other Offer Details *</label>
                      <input
                        type="text"
                        value={campaignForm.otherOfferDetails}
                        onChange={(e) => handleCampaignFormChange('otherOfferDetails', e.target.value)}
                        placeholder="Please specify the offer details"
                        className="form-input"
                        disabled={uploading}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Coupon Code (if applicable)</label>
                    <input
                      type="text"
                      value={campaignForm.coupon}
                      onChange={(e) => handleCampaignFormChange('coupon', e.target.value)}
                      placeholder="Enter coupon code"
                      className="form-input"
                      disabled={uploading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      value={campaignForm.campaignName}
                      onChange={(e) => handleCampaignFormChange('campaignName', e.target.value)}
                      placeholder="Auto-generated; edit if needed"
                      className="form-input"
                      disabled={uploading}
                    />
                  </div>
                </div>
              </div>

              {/* CSV Format Instructions */}
              <div className="csv-format-info">
                <div className="format-info-header">
                  <Info size={18} />
                  <span>CSV Format Requirements</span>
                </div>
                <div className="format-info-content">
                  <p className="format-description">
                    CSV should contain only: <strong>email</strong> or <strong>phone</strong> (at least one required per row)
                  </p>
                  <p className="format-description">
                    The campaign information entered above will be applied to all customers in the CSV file.
                  </p>
                  <button 
                    className="download-sample-btn"
                    onClick={downloadSampleCSV}
                    type="button"
                    disabled={uploading}
                  >
                    <Download size={14} />
                    Download Sample CSV
                  </button>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                className={`campaign-upload-area ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="file-selected">
                    <FileText size={48} className="file-icon" />
                    <div className="file-details">
                      <p className="file-name">{selectedFile.name}</p>
                      <p className="file-size">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <button
                      className="remove-file-btn"
                      onClick={handleRemoveFile}
                      disabled={uploading}
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="file-upload-placeholder">
                    <Upload size={48} className="upload-icon" />
                    <p className="upload-text">
                      Drag and drop your CSV file here, or{' '}
                      <label className="upload-label">
                        browse
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileInputChange}
                          className="file-input"
                          disabled={uploading}
                        />
                      </label>
                    </p>
                    <p className="upload-hint">CSV file (max 50MB)</p>
                  </div>
                )}
              </div>

              {/* Preview Rows */}
              {previewRows.length > 0 && (
                <div className="preview-section">
                  <p className="preview-title">File Preview (first {previewRows.length} rows):</p>
                  <div className="preview-table">
                    {previewRows.map((row, index) => (
                      <div key={index} className="preview-row">
                        {row.split(',').slice(0, 6).map((cell, cellIndex) => (
                          <span key={cellIndex} className="preview-cell">
                            {cell.trim().replace(/^"|"$/g, '')}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="upload-progress-section">
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="progress-text">
                    {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Saving campaign… almost done…'}
                  </p>
                </div>
              )}

              {/* Upload Result */}
              {uploadResult && !uploading && (
                <div className={`upload-result ${uploadResult.success ? 'success' : 'error'}`}>
                  {uploadResult.success ? (
                    <>
                      <CheckCircle size={20} />
                      <div className="result-content">
                        <p className="result-title">Upload Successful!</p>
                        {uploadResult.stats && (
                          <div className="result-stats">
                            <p>Total Rows: {uploadResult.stats.totalRows}</p>
                            <p>Valid Rows: {uploadResult.stats.validRows}</p>
                            <p>Invalid Rows: {uploadResult.stats.invalidRows}</p>
                            <p>Customers Updated: {uploadResult.stats.customersUpdated}</p>
                            {uploadResult.stats.customersCreated > 0 && (
                              <p>New customers created (phone-only): {uploadResult.stats.customersCreated}</p>
                            )}
                          </div>
                        )}
                        {uploadResult.campaigns && uploadResult.campaigns.length > 0 && (
                          <div className="campaigns-list">
                            <p className="campaigns-title">Campaigns:</p>
                            <ul>
                              {uploadResult.campaigns.map((campaign, index) => (
                                <li key={index}>
                                  {campaign.campaignName} ({campaign.source}) - {campaign.customersCount} customers
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={20} />
                      <div className="result-content">
                        <p className="result-title">Upload Failed</p>
                        <p className="result-message">{uploadResult.message}</p>
                      </div>
                    </>
                  )}
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="errors-list">
                      <p className="errors-title">Errors ({uploadResult.errors.length}):</p>
                      <ul>
                        {uploadResult.errors.slice(0, 10).map((error, index) => (
                          <li key={index}>
                            Row {error.row}: {error.error} {error.email && `(${error.email})`}
                          </li>
                        ))}
                        {uploadResult.errors.length > 10 && (
                          <li>... and {uploadResult.errors.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* File Error */}
              {fileError && (
                <div className="file-error">
                  <AlertCircle size={16} />
                  <span>{fileError}</span>
                </div>
              )}

              {/* Actions */}
              <div className="campaign-upload-actions">
                <button
                  className="cancel-btn"
                  onClick={handleClose}
                  disabled={uploading}
                >
                  {uploadResult ? 'Close' : 'Cancel'}
                </button>
                {!uploadResult && (
                  <button
                    className="upload-btn"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? (
                      <>
                        <div className="spinner-small"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        Upload Campaign CSV
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CampaignUploadModal;
